/**
 * MOVIT's addresses (docs/parity/frontend-inventory-1-core.md §0, 52 routes), sent to their Nova
 * places so old bookmarks and links in old e-mails land somewhere useful instead of "page not
 * found". Only redirects: the page they land on declares its permission (RouteAccess), so a
 * person without it gets the usual "no access" page there.
 *
 * MOVIT's query parameters (`?project=`, `?company=`, record ids) are not carried over: its ids
 * are not Nova's. Addresses with a capability in them (`/shared-report/:key`, the TV leaderboard,
 * `/communication` SMS links) have no Nova equivalent — those tokens were MOVIT's — and are left
 * out, as is `/taxi` (no taxi integration yet). `/trips`, `/events`, `/devices`, `/live` and
 * `/media-module` are Nova addresses too (their modules redirect or serve them).
 */
import { createRoute, redirect } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { publicRoute } from '@/app/publicRoute';
import { str } from '@/lib/search';

/** Old path → Nova address (path and search), for the docs and the test. */
export const LEGACY_REDIRECTS = new Map<string, string>();

/** A MOVIT address inside the app (sign-in first, then the redirect). */
function moved<const P extends string>(path: P, to: string) {
  LEGACY_REDIRECTS.set(path, to);
  return createRoute({
    getParentRoute: () => appRoute,
    path,
    beforeLoad: () => {
      throw redirect({ href: to, replace: true });
    },
  });
}

/** A MOVIT sign-in address (no session needed). */
function movedPublic<const P extends string>(path: P, to: string) {
  LEGACY_REDIRECTS.set(path, to);
  return createRoute({
    getParentRoute: () => publicRoute,
    path,
    beforeLoad: () => {
      throw redirect({ href: to, replace: true });
    },
  });
}

export const legacyRoutes = [
  // Live dashboard and the dashboards around it (IA §2)
  moved('/main', '/live'),
  moved('/loadingscreen', '/'),
  moved('/project-dashboard', '/insights'),
  moved('/project-data', '/insights'),
  moved('/my-crew', '/insights/drivers'),
  moved('/scores', '/insights/drivers'),
  // Fleet
  moved('/vehicle', '/fleet/vehicles'),
  moved('/driver', '/fleet/drivers'),
  moved('/asset-tracking', '/fleet/assets'),
  moved('/howen/devices', '/fleet/devices'),
  moved('/howen/status', '/quality/devices'),
  moved('/howen/validate-installation', '/fleet/devices'),
  moved('/howen/installation-checks', '/fleet/devices'),
  moved('/howen/installation-check-detail', '/fleet/devices'),
  // Places and rules
  moved('/locations', '/live/places'),
  moved('/visits', '/live/visits'),
  moved('/rules', '/fleet/asset-rules'),
  // Automation (alerting flows and their prototypes)
  moved('/alerting-module', '/automation'),
  moved('/proto/alerting-table', '/automation/runs'),
  moved('/proto/alerting-timeline', '/automation/runs'),
  // Video
  moved('/media-module/existing-video', '/activity/video'),
  moved('/media-module/request-video', '/activity/video/request'),
  // Coaching
  moved('/coaching/center', '/coaching'),
  moved('/coaching/logbook', '/coaching?tab=cases&view=logbook'),
  moved('/coaching/quick-action', '/coaching?tab=queue'),
  moved('/coaching/trip-comparison', '/activity/trips/compare'),
  moved('/coaching/system-overview', '/coaching'),
  // Compliance and insurance
  moved('/tachograph', '/compliance'),
  moved('/tachograph/files/$fileUuid', '/compliance/files'),
  moved('/tacho-live', '/compliance/hours'),
  moved('/insurance-hub', '/insurance'),
  moved('/insurance-hub/drivers', '/insurance/drivers'),
  moved('/insurance-hub/coaching', '/insurance/coaching'),
  // Reports
  moved('/report-generator', '/reports'),
  // Administration and my account
  moved('/project', '/admin/projects'),
  moved('/company', '/admin/company'),
  moved('/user', '/admin/users'),
  moved('/api-keys', '/admin/api-keys'),
  moved('/core-settings', '/admin/settings'),
  moved('/news', '/'),
  moved('/change-password', '/me/password'),
] as const;

export const legacyPublicRoutes = [
  // The e-mail from "forgot password" in MOVIT carried the address; keep it.
  createRoute({
    getParentRoute: () => publicRoute,
    path: '/forgot-password',
    beforeLoad: ({ search }) => {
      const email = str((search as Record<string, unknown>).email);
      throw redirect({ href: email ? `/forgot?email=${encodeURIComponent(email)}` : '/forgot', replace: true });
    },
  }),
  // A MOVIT reset code does not work in Nova: ask for a new link.
  movedPublic('/reset-password', '/forgot'),
  movedPublic('/auth/callback', '/login'),
] as const;
LEGACY_REDIRECTS.set('/forgot-password', '/forgot');
